import { TabStyles } from '@tableau/widgets';
import { CommonErrorCode, UserErrorCode } from '../../ts/helpers/ErrorCodes';
import { L10n } from '../../ts/L10n/L10n';
import { IDataAlertInfo } from '../../ts/models/IDataAlertInfo';
import { IUserInfo } from '../../ts/models/IUserInfo';
import { IUserSettings } from '../../ts/models/IUserSettings';
import { IErrorItem } from '../../ts/models/MultipleItemResult';
import { Set } from '../../ts/models/Set';
import { DataAlerts } from '../../ts/resources/DataAlerts';
import { ServerUsers } from '../../ts/resources/ServerUsers';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { ActionHelpers } from '../../ts/services/ActionHelpers';
import { IErrorDetail } from '../../ts/services/ContentActionNotification';
import { ModalService } from '../../ts/services/ModalService';
import { ServerService } from '../../ts/services/ServerService';
import { ReactConfirmActionDialogModule } from '../../ts/services/ReactConfirmActionDialog';
import { Messages, Formatters } from '../../codegen/Localize';
import { ReactAlertStringBodyProps, ReactAlertStringBody, DdaReactAlertDialog} from '../components/ReactAlertDialog';
import { IUserActionResult } from '../../ts/ServerApi/types/IUserActionResult';
import { ServiceLocator } from '../../app/ServiceLocator';


function  addSelfToAlert(alerts: Set<IDataAlertInfo>): Promise<any> {

    const ActionHelpers = ServiceLocator.get<ActionHelpers>('ActionHelpers');
    const ServerService = ServiceLocator.get<ServerService>('ServerService');
    const DataAlerts = ServiceLocator.get<DataAlerts>('DataAlerts');
    const ServerUsers = ServiceLocator.get<ServerUsers>('ServerUsers');
    const self = ServerService.sessionUserInfo();

    function getUserEmailAddress(self: IUserInfo): Promise<string> {
        return Promise.resolve(ServerUsers.getUserSettings(self.username, self.domainName)
            .then((userSettings: IUserSettings) => userSettings && userSettings.email));
    }

    function showConfirmDialog(alerts: Set<IDataAlertInfo>, self: IUserInfo) {

      let dialogBodyText: string;
      let dialogTitle: string;
      if (alerts.size() === 1) {
        dialogTitle = Messages.confirmAddSelfToAlertTitle();
        dialogBodyText = Messages.confirmAddSelfToAlert({
          ALERT_NAME: L10n.elideIfLong(alerts.first().name)
        });
      } else {
        dialogTitle = Messages.confirmAddSelfToAlertsTitle();
        dialogBodyText = Messages.confirmAddSelfToAlerts({
          ALERT_COUNT: alerts.size(),
          ALERT_COUNT_LOC: Formatters.formatCount(alerts.size())
        });
      }

      const dialogInstance = ReactConfirmActionDialogModule.openConfirmActionDialog<ReactAlertStringBodyProps, any, IUserActionResult>({
        title: null,
        dialogBodyStyle : {
          paddingTop: TabStyles.Spacing.LargeSpace,
          paddingBottom: TabStyles.Spacing.LargeSpace},
        bodyComponent: ReactAlertStringBody,
        initialBodyProps: {
          title: dialogTitle,
          message: dialogBodyText
        },
        initialDialogState: {},
        actionName: Messages.confirmAddSelfButton(),
        action: () => DataAlerts.addUsers({ dataAlertIds: alerts.keys(), userIds: [ self.id ] }),
        isActionDelete: false
      });

      dialogInstance.result.then((result: IUserActionResult) => {
        notifyResult(alerts, result);
      });

      return dialogInstance.result;
    }

    function showErrorDialogMissingEmail(alerts: Set<IDataAlertInfo>) {
      const title = Messages.errorDataAlertCannotAddTitle(
            { ITEM_COUNT: alerts.size() });
      const message = Messages.errorDataAlertNoEmailAddress();
      return DdaReactAlertDialog.openAlertDialog({title: title, message: message}).result;
    }

    function notifyResult(alerts: Set<IDataAlertInfo>, result: IResult) {
      ActionHelpers.notifyStandardError(
        alerts,
        {
          successId: 'addSelfToAlertsSuccess',
          errorId: 'addSelfToAlertsError'
        },
        (errorItem: IErrorItem<IDataAlertInfo>) => errorDetailFor(errorItem)
      )(result);
    }

    function errorDetailFor(errorItem: IErrorItem<IDataAlertInfo>): IErrorDetail {
      const itemName = errorItem.item.name;
      let errorMessage: string;

      switch (errorItem.error.code) {
        case CommonErrorCode.DataAlertNotFound:
          errorMessage = Messages.errorDataAlertNotFound({ ALERT_NAME: L10n.elideIfLong(itemName) });
          break;
        case CommonErrorCode.CallingUserInsufficientPermissions:
          errorMessage = Messages.errorDataAlertNoPermission({ ALERT_NAME: L10n.elideIfLong(itemName) });
          break;
        case UserErrorCode.UserInvalidEmailAddress:
          errorMessage = Messages.errorDataAlertNoEmailAddress();
          break;
        default:
          errorMessage = Messages.errorUnknown();
      }

      return { itemName, errorMessage };
    }

    return getUserEmailAddress(self).then((emailAddress: string) => {
      if (emailAddress) {
        return showConfirmDialog(alerts, self);
      } else {
        return showErrorDialogMissingEmail(alerts);
      }
    });
}

export const AddSelfToAlertsAction = {
    addSelfToAlert,
  };